/**
 * LOINC codes for vital signs — copied verbatim from Flutter
 * `scrips_core/lib/constants/app_constants.dart:259-275`. These ARE the
 * wire values: the backend's vitals controller is a thin pass-through
 * that keys on `code.code`, so any drift here silently breaks
 * server-side normal-range routing.
 *
 * The 2 sentinel codes (`bioage = "11-5"`, `bmi = "39156-5"`) are
 * Scrips-local, not real LOINC.
 *
 * @see {@link ./vital-catalog.ts} — UI metadata keyed by these codes
 */

export const VITAL_CODES = {
  /** Body temperature. */
  TEMPERATURE: '8310-5',
  /** Heart rate / pulse. */
  HEART_RATE: '8867-4',
  /** Systolic BP. Combo-renders with diastolic into a single cell. */
  SYSTOLIC: '8480-6',
  /** Diastolic BP. */
  DIASTOLIC: '8462-4',
  /** Respiratory rate. */
  RESP_RATE: '9279-1',
  /** Oxygen saturation. */
  O2_SAT: '59408-5',
  /** Height. */
  HEIGHT: '8302-2',
  /** Weight. */
  WEIGHT: '29463-7',
  /** BMI — computed by backend from height/weight; UI cell is read-only. */
  BMI: '39156-5',
  /** Last menstrual period (date). Gated on gender=Female, age>8. */
  LMP: '8665-2',
  /** Head circumference. Gated on age <= 2. */
  HCM: '11947-9',
  /** Hearing test result (free-text). */
  HEARING: '62977-4',
  /** Visual acuity. Stores `20/20/20/20` plus an `extraData` blob. */
  VISUAL: '31479-8',
  /** Bioage. Scrips-local code (not real LOINC). */
  BIOAGE: '11-5',
  /** Hip circumference. */
  HIP: '8281-8',
  /** Waist circumference. */
  WAIST: '8280-0',
  /** Waist-to-hip ratio — computed by backend from hip/waist. */
  WAIST_TO_HIP_RATIO: '56115-9',
} as const;

export type VitalCodeKey = keyof typeof VITAL_CODES;
export type VitalCodeValue = (typeof VITAL_CODES)[VitalCodeKey];

/**
 * Set of codes the backend computes — UI should NOT accept user input
 * for these. Flutter `vital_utils.dart` and `emptyVitalUiList` mark
 * them `isEditable: false`.
 */
export const COMPUTED_VITAL_CODES: ReadonlySet<string> = new Set([
  VITAL_CODES.BMI,
  VITAL_CODES.WAIST_TO_HIP_RATIO,
]);
